import { useState } from 'react';
import { StarRating } from './StarRating';
import { Button } from '../ui/Button';

interface ReviewFormProps {
  productSlug: string;
  onSubmitted?: () => void;
}

export function ReviewForm({ productSlug, onSubmitted }: ReviewFormProps) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`/api/products/${productSlug}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rating, title, body: body || undefined }),
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Failed to submit review');
      }

      setRating(0);
      setTitle('');
      setBody('');
      onSubmitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Rating</label>
        <StarRating rating={rating} onChange={setRating} size="lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          maxLength={100}
          className="w-full rounded-lg border border-white/10 bg-navy-900 px-3 py-2 text-white placeholder-gray-500 focus:border-green-600 focus:outline-none"
          placeholder="Summarize your experience"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Review (optional)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={3}
          className="w-full rounded-lg border border-white/10 bg-navy-900 px-3 py-2 text-white placeholder-gray-500 focus:border-green-600 focus:outline-none"
          placeholder="Share your thoughts..."
        />
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <Button type="submit" isLoading={isSubmitting}>Submit Review</Button>
    </form>
  );
}
